import type { CardFooterProps, FlexProps } from '@chakra-ui/react'
import { CardFooter, Flex, useMediaQuery } from '@chakra-ui/react'
import type { Asset } from '@shapeshiftoss/types'
import noop from 'lodash/noop'
import type { JSX } from 'react'
import { useCallback, useMemo } from 'react'

import { ButtonWalletPredicate } from '@/components/ButtonWalletPredicate/ButtonWalletPredicate'
import { FiatRampAction } from '@/components/Modals/FiatRamps/FiatRampsCommon'
import { RateGasRow } from '@/components/MultiHopTrade/components/RateGasRow'
import { SharedRecipientAddress } from '@/components/MultiHopTrade/components/SharedTradeInput/SharedRecipientAddress'
import { Protocol } from '@/components/MultiHopTrade/components/TradeInput/components/Protocol'
import { useReceiveAddress } from '@/components/MultiHopTrade/hooks/useReceiveAddress'
import { Text } from '@/components/Text'
import { useDiscoverAccounts } from '@/context/AppProvider/hooks/useDiscoverAccounts'
import { bnOrZero } from '@/lib/bignumber/bignumber'
import { selectFeeAssetById } from '@/state/slices/selectors'
import {
  selectBuyAccountId,
  selectBuyFiatCurrency,
  selectManualReceiveAddress,
  selectSelectedBuyFiatRampQuote,
  selectSelectedSellFiatRampQuote,
  selectSellFiatCurrency,
} from '@/state/slices/tradeRampInputSlice/selectors'
import { tradeRampInput } from '@/state/slices/tradeRampInputSlice/tradeRampInputSlice'
import { useAppDispatch, useAppSelector } from '@/state/store'
import { breakpoints } from '@/theme/theme'

const rateGasRowSx: FlexProps = {
  pb: 0,
}

type FiatRampTradeFooterProps = {
  children?: JSX.Element
  hasUserEnteredAmount: boolean
  isError: boolean
  isLoading: boolean
  rate: string | undefined
  shouldDisablePreviewButton: boolean | undefined
  networkFeeFiatUserCurrency: string | undefined
  marketRate?: string
  invertRate?: boolean
  quoteStatusTranslation: string
  noExpand?: boolean
  icon: React.ReactNode
  onOpenQuoteList?: () => void
} & (
  | {
      direction: FiatRampAction.Buy
      buyAsset: Asset
      sellAsset?: never
      sellAccountId?: never
    }
  | {
      direction: FiatRampAction.Sell
      sellAsset: Asset
      sellAccountId: string | undefined
      buyAsset?: never
    }
)

const footerBgProp = {
  base: 'background.surface.base',
  md: 'transparent',
}
const footerPosition: CardFooterProps['position'] = { base: 'sticky', md: 'static' }

export const FiatRampTradeFooter = ({
  children,
  hasUserEnteredAmount,
  isError,
  isLoading: isParentLoading,
  rate,
  shouldDisablePreviewButton: parentShouldDisablePreviewButton,
  networkFeeFiatUserCurrency,
  marketRate,
  invertRate,
  noExpand,
  quoteStatusTranslation,
  direction,
  icon,
  onOpenQuoteList,
  ...props
}: FiatRampTradeFooterProps) => {
  const buyAsset = 'buyAsset' in props ? props.buyAsset : undefined
  const sellAsset = 'sellAsset' in props ? props.sellAsset : undefined
  const sellAccountId = 'sellAccountId' in props ? props.sellAccountId : undefined
  const buyAssetFeeAsset = useAppSelector(state =>
    selectFeeAssetById(state, buyAsset?.assetId ?? ''),
  )
  const dispatch = useAppDispatch()
  const selectedBuyQuote = useAppSelector(selectSelectedBuyFiatRampQuote)
  const selectedSellQuote = useAppSelector(selectSelectedSellFiatRampQuote)
  const buyAccountId = useAppSelector(selectBuyAccountId)
  const manualReceiveAddress = useAppSelector(selectManualReceiveAddress)
  const sellFiatCurrency = useAppSelector(selectSellFiatCurrency)
  const buyFiatCurrency = useAppSelector(selectBuyFiatCurrency)

  const { walletReceiveAddress, isLoading: isWalletReceiveAddressLoading } = useReceiveAddress({
    sellAccountId: undefined,
    buyAccountId,
    buyAsset,
  })

  const [isSmallerThanXl] = useMediaQuery(`(max-width: ${breakpoints.xl})`, { ssr: false })

  const selectedQuote = useMemo(
    () => (direction === FiatRampAction.Buy ? selectedBuyQuote : selectedSellQuote),
    [direction, selectedBuyQuote, selectedSellQuote],
  )

  const { isFetching: isDiscoveringAccounts } = useDiscoverAccounts()

  const isLoading = useMemo(() => {
    return isParentLoading || (!buyAssetFeeAsset && direction === FiatRampAction.Buy)
  }, [buyAssetFeeAsset, isParentLoading, direction])

  const shouldDisablePreviewButton = useMemo(() => {
    return (
      parentShouldDisablePreviewButton ||
      (isDiscoveringAccounts && !sellAccountId) ||
      // don't allow executing a quote with errors
      isError ||
      // don't execute trades while in loading state
      isLoading
    )
  }, [parentShouldDisablePreviewButton, isDiscoveringAccounts, sellAccountId, isError, isLoading])

  const deltaPercentage = useMemo(() => {
    if (!rate || !marketRate || bnOrZero(marketRate).isZero()) return null

    const percentDifference = bnOrZero(rate).minus(marketRate).div(marketRate).times(100)

    if (percentDifference.isZero()) return null

    return percentDifference
  }, [rate, marketRate])

  const buttonText = useMemo(() => {
    return <Text translation={quoteStatusTranslation} />
  }, [quoteStatusTranslation])

  const handleIsValidatingChange = useCallback(
    (isValidating: boolean) => {
      dispatch(tradeRampInput.actions.setIsManualReceiveAddressValidating(isValidating))
    },
    [dispatch],
  )

  const handleIsValidChange = useCallback(
    (isValid: boolean) => {
      dispatch(tradeRampInput.actions.setIsManualReceiveAddressValid(isValid))
    },
    [dispatch],
  )

  const handleError = useCallback(() => {
    dispatch(tradeRampInput.actions.setManualReceiveAddress(undefined))
  }, [dispatch])

  const handleEdit = useCallback(() => {
    dispatch(tradeRampInput.actions.setIsManualReceiveAddressEditing(true))
  }, [dispatch])

  const handleCancel = useCallback(() => {
    dispatch(tradeRampInput.actions.setIsManualReceiveAddressEditing(false))
    dispatch(tradeRampInput.actions.setIsManualReceiveAddressValid(undefined))
  }, [dispatch])

  const handleReset = useCallback(() => {
    dispatch(tradeRampInput.actions.setManualReceiveAddress(undefined))
    dispatch(tradeRampInput.actions.setIsManualReceiveAddressValid(undefined))
  }, [dispatch])

  const handleSubmit = useCallback(
    (address: string) => {
      dispatch(tradeRampInput.actions.setManualReceiveAddress(address))
      dispatch(tradeRampInput.actions.setIsManualReceiveAddressEditing(false))
    },
    [dispatch],
  )

  return (
    <CardFooter
      flexDir='column'
      px={0}
      py={0}
      position={footerPosition}
      bottom={'var(--mobile-nav-offset)'}
      bg={footerBgProp}
      borderTopWidth={1}
      borderColor={'border.subtle'}
    >
      {hasUserEnteredAmount && selectedQuote && !isLoading && (
        <RateGasRow
          affiliateBps={'0'}
          buyAssetSymbol={
            direction === FiatRampAction.Buy && buyAsset
              ? buyAsset.symbol
              : buyFiatCurrency?.code ?? 'USD'
          }
          sellAssetSymbol={
            direction === FiatRampAction.Buy && sellFiatCurrency
              ? sellFiatCurrency.code
              : sellAsset?.symbol ?? 'USD'
          }
          rate={rate}
          deltaPercentage={deltaPercentage?.toString()}
          isLoading={isLoading && !rate}
          networkFeeFiatUserCurrency={networkFeeFiatUserCurrency}
          invertRate={invertRate}
          icon={icon}
          noExpand={noExpand}
          hideGasAmount
          flexProps={rateGasRowSx}
          enableFeeTooltip={false}
        />
      )}
      <Flex
        flexDir='column'
        gap={4}
        px={4}
        py={4}
        bg={footerBgProp}
        borderBottomRadius='xl'
        width='full'
      >
        {direction === FiatRampAction.Buy && buyAsset && (
          <SharedRecipientAddress
            buyAsset={buyAsset}
            isWalletReceiveAddressLoading={isWalletReceiveAddressLoading}
            walletReceiveAddress={walletReceiveAddress}
            manualReceiveAddress={manualReceiveAddress}
            onCancel={handleCancel}
            onEdit={handleEdit}
            onError={handleError}
            onIsValidatingChange={handleIsValidatingChange}
            onIsValidChange={handleIsValidChange}
            onReset={handleReset}
            onSubmit={handleSubmit}
          />
        )}

        {isSmallerThanXl && (
          <Protocol
            onClick={onOpenQuoteList || noop}
            isLoading={isParentLoading}
            title={selectedQuote?.provider}
            icon={icon}
          />
        )}

        {children}

        <ButtonWalletPredicate
          isLoading={isLoading || isDiscoveringAccounts}
          loadingText={isLoading ? undefined : buttonText}
          type='submit'
          colorScheme={isError ? 'red' : 'blue'}
          size='lg-multiline'
          data-test='trade-form-preview-button'
          isDisabled={shouldDisablePreviewButton}
          isValidWallet
        >
          {buttonText}
        </ButtonWalletPredicate>
      </Flex>
    </CardFooter>
  )
}
